import type { LocalHourPoint } from '../types/traffic'

export interface LocalHourInsight {
  totalRequests: number
  peakHour: number
  peakRequests: number
  /** Share of requests between midnight and 6am on the visitor's own clock. */
  overnightShare: number
  /** Share between 9am and 6pm — normal working hours. */
  workdayShare: number
  /** Contiguous hours with no traffic at all, if there is a clear stretch. */
  quietFrom: number | null
  quietTo: number | null
}

function shareBetween(points: LocalHourPoint[], from: number, to: number, total: number): number {
  if (total === 0) return 0
  const inRange = points
    .filter(point => point.hour >= from && point.hour < to)
    .reduce((sum, point) => sum + point.requests, 0)
  return inRange / total
}

/**
 * Longest run of hours with zero requests. Reported only when it is long enough to
 * be meaningful (4+ hours), since short gaps are just sampling noise.
 */
function longestQuietRun(points: LocalHourPoint[]): { from: number; to: number } | null {
  const byHour = new Map(points.map(point => [point.hour, point.requests]))
  let best: { from: number; to: number } | null = null
  let start: number | null = null

  for (let hour = 0; hour <= 24; hour += 1) {
    const isQuiet = hour < 24 && (byHour.get(hour) ?? 0) === 0

    if (isQuiet && start === null) start = hour
    if (!isQuiet && start !== null) {
      const length = hour - start
      if (length >= 4 && (best === null || length > best.to - best.from)) {
        best = { from: start, to: hour }
      }
      start = null
    }
  }

  return best
}

export function summarizeLocalHours(points: LocalHourPoint[]): LocalHourInsight | null {
  const totalRequests = points.reduce((sum, point) => sum + point.requests, 0)
  if (totalRequests === 0) return null

  const peak = points.reduce((best, point) => (point.requests > best.requests ? point : best))
  const quiet = longestQuietRun(points)

  return {
    totalRequests,
    peakHour: peak.hour,
    peakRequests: peak.requests,
    overnightShare: shareBetween(points, 0, 6, totalRequests),
    workdayShare: shareBetween(points, 9, 18, totalRequests),
    quietFrom: quiet?.from ?? null,
    quietTo: quiet?.to ?? null
  }
}

/** '9am', '12pm', '11pm' */
export function hourText(hour: number): string {
  if (hour === 0) return '12am'
  if (hour === 12) return '12pm'
  return hour < 12 ? `${hour}am` : `${hour - 12}pm`
}
